'use client';

import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_LABEL_TEXT,
  AttributePopoverConfirm
} from '@/components/attributes';
import { cn } from '@/lib/utils';

/**
 * Shell for an inline identity-variant editor (LF-5714).
 *
 * Replaces the previous nested-dialog editors. This section renders inside the
 * contact drawer and the Edit-matter dialog, and opening a Radix dialog from
 * within one of those dismissed the whole host surface on save or cancel — the
 * lawyer lost their place in the form and any unsaved parent-form edits. So a
 * variant is edited in place, where it sits.
 *
 * Deliberately **not** a `<form>`: both host surfaces are themselves `<form>`
 * elements, so a nested form is invalid HTML and its submit would bubble to the
 * parent and save the whole contact/matter. Enter and Escape are handled here
 * instead, and the confirm control is a plain `type="button"`.
 */

interface VariantInlineEditorProps {
  /** Names what is being edited, e.g. "Edit name" / "Add an address". */
  title: string;
  /** Confirm-button label, e.g. "Save name". */
  submitLabel: string;
  canSubmit: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /**
   * **Drops the card chrome, for a host that already draws a panel**
   * (VALE-863) — the attribute rows edit a variant inside the shared composite
   * popover, and a bordered grey card inside a popover is a box in a box.
   *
   * The keyboard contract is untouched and is the reason this stays one shell
   * rather than two: `Escape` and `Enter` must still be intercepted here in
   * both hosts, because the drawer and the Edit-matter dialog are `<form>`
   * elements and either key would otherwise reach them.
   */
  bare?: boolean;
  /**
   * **Commit-on-dismiss: the two-button footer is dropped for the shared confirm**
   * (VALE-863, amended by VALE-1710).
   *
   * VALE-863 drew nothing here, reasoning that *"a Save button beside an editor
   * that has already saved is worse than no button"* and that commit-on-dismiss
   * was *"the contract every other editor on this treatment already keeps"*.
   * The first half still holds — a full `Cancel` / `Save name` footer inside a
   * composite popover is a second commit model, and `Escape` already cancels.
   * **The second half is what VALE-1710 reversed**: every other composite
   * popover on this treatment now carries a visible control that finishes the
   * edit, so drawing nothing made these two the odd ones out instead of the
   * ones in line. Reported on the sign-off pass, on exactly those words —
   * *"other name and other addresses dont have it"*.
   *
   * So this arm renders {@link AttributePopoverConfirm}, the same controls the
   * hook-bound panels get: Done wired to the same `onSubmit` that `Enter`
   * already fires below, and Cancel (VALE-2080) to the same `onCancel` that
   * `Escape` fires. The words and the corner come from the shared module so
   * they cannot drift from the other eight panels. It is still not the
   * two-button footer below: dismissing commits here, where the footer's host
   * has no dismissal at all.
   *
   * Removing a variant lives on the row's own clear affordance in this mode,
   * beside copy, where every other row's clear is.
   */
  autoSave?: boolean;
  children: ReactNode;
}

export function VariantInlineEditor({
  title,
  submitLabel,
  canSubmit,
  isSubmitting,
  onSubmit,
  onCancel,
  bare = false,
  autoSave = false,
  children
}: VariantInlineEditorProps) {
  return (
    <div
      className={cn(
        ATTRIBUTE_COMPOSITE_PARTS,
        !bare && 'bg-muted/40 border-border my-2 rounded-md border p-2.5'
      )}
      onKeyDown={(event) => {
        // Escape would otherwise bubble up and dismiss the host drawer/dialog.
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
          return;
        }
        // Enter in a single-line input submits the nearest form — here, the
        // host contact/matter form. Intercept it and save just this row.
        if (
          event.key === 'Enter' &&
          !(event.target instanceof HTMLTextAreaElement)
        ) {
          event.preventDefault();
          if (canSubmit && !isSubmitting) onSubmit();
        }
      }}
    >
      <p className={cn(ATTRIBUTE_LABEL_TEXT, 'text-foreground font-medium')}>
        {title}
      </p>

      {children}

      {autoSave ? (
        /*
          `disabled` on the same test `Enter` uses below, rather than a live
          button that silently does nothing on an incomplete variant. This is
          the one difference from a hook-bound panel, and it is a real one: there
          a refusal comes from `validate` at commit time and is MEANT to be
          reachable, because the refusal message is how the reader is told what
          is wrong. Here the form knows before committing.
        */
        <AttributePopoverConfirm
          onConfirm={onSubmit}
          disabled={!canSubmit || isSubmitting}
          // The same `onCancel` `Escape` fires above, which drops the draft
          // before closing so the dismiss that follows saves nothing (VALE-2080).
          onCancel={onCancel}
          cancelDisabled={isSubmitting}
        />
      ) : (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="xs"
            density="compact"
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            density="compact"
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
